import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type {
  BillingAccountRow,
  BillingEventPage,
  BillingEventRow,
} from "./types";

const client = generateClient<Schema>();

// ── Secondary-index cast helper ───────────────────────────────────────────────
// Amplify Gen 2 generates listBy* methods from secondary indexes at runtime but
// does not expose them in the generated TypeScript types. Access via narrowly-
// scoped cast, mirroring the pattern in money_repository.ts.

interface EventRecord {
  id: string;
  provider?: string | null;
  action: string;
  previousState?: string | null;
  newState?: string | null;
  previousPlan?: string | null;
  newPlan?: string | null;
  previousActive?: boolean | null;
  newActive?: boolean | null;
  previousAutoRenew?: boolean | null;
  newAutoRenew?: boolean | null;
  effectiveAt?: string | null;
  occurredAt: string;
  sourceEventId?: string | null;
  correlationId?: string | null;
}

type ListByResult = Promise<{
  data: EventRecord[];
  nextToken?: string | null;
  errors?: { message: string }[];
}>;

interface ListByOpts {
  sortDirection?: "ASC" | "DESC";
  limit?: number;
  nextToken?: string | null;
}

function listBillingEventByBusinessIdAndOccurredAt(
  key: { businessId: string },
  opts: ListByOpts,
): ListByResult {
  const model = client.models.BillingEvent as unknown as {
    listBillingEventByBusinessIdAndOccurredAt: (
      key: { businessId: string },
      opts: ListByOpts,
    ) => ListByResult;
  };
  return model.listBillingEventByBusinessIdAndOccurredAt(key, opts);
}

/** How many timeline entries a page holds. */
export const EVENT_PAGE_SIZE = 50;

export class BillingRepository {
  /**
   * The merchant's projection row, or `null` when none exists.
   *
   * Absent is a real answer rather than an error: the projection is written by
   * the Stripe event path and the nightly sweep, so a merchant who signed up
   * since the last sweep genuinely has no row yet.
   */
  async getAccount(businessId: string): Promise<BillingAccountRow | null> {
    const { data, errors } = await client.models.BillingAccount.get({
      businessId,
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data) return null;

    return {
      businessId: data.businessId,
      provider: data.provider ?? null,
      state: data.state ?? null,
      plan: data.plan ?? null,
      active: data.active ?? null,
      periodEnd: data.periodEnd ?? null,
      autoRenew: data.autoRenew ?? null,
      canceledAt: data.canceledAt ?? null,
      trialConsumedAt: data.trialConsumedAt ?? null,
      stripeSubscriptionId: data.stripeSubscriptionId ?? null,
      iapAccountToken: data.iapAccountToken ?? null,
      version: data.version ?? null,
      createdAt: data.createdAt ?? null,
      updatedAt: data.updatedAt ?? null,
    };
  }

  /** One page of the merchant's history, newest first. */
  async listEvents(
    businessId: string,
    nextToken?: string | null,
  ): Promise<BillingEventPage> {
    const page = await listBillingEventByBusinessIdAndOccurredAt(
      { businessId },
      { sortDirection: "DESC", limit: EVENT_PAGE_SIZE, nextToken },
    );
    if (page.errors?.length) throw new Error(page.errors[0].message);

    const items: BillingEventRow[] = (page.data ?? []).map((e) => ({
      id: e.id,
      provider: e.provider ?? null,
      action: e.action,
      previousState: e.previousState ?? null,
      newState: e.newState ?? null,
      previousPlan: e.previousPlan ?? null,
      newPlan: e.newPlan ?? null,
      previousActive: e.previousActive ?? null,
      newActive: e.newActive ?? null,
      previousAutoRenew: e.previousAutoRenew ?? null,
      newAutoRenew: e.newAutoRenew ?? null,
      effectiveAt: e.effectiveAt ?? null,
      occurredAt: e.occurredAt,
      sourceEventId: e.sourceEventId ?? null,
      correlationId: e.correlationId ?? null,
    }));

    return { items, nextToken: page.nextToken ?? null };
  }
}

export const billingRepository = new BillingRepository();
